import { apiClient } from '@/api/client'
import { ApiError, parseProblemDetails } from '@/api/problemDetails'

import type { AgentModeChange, AgentModeValue, AgentSummary, PlaygroundResult } from './types'

/**
 * Os endpoints de agentes (WP-08a/§24) tem `.Produces<T>()` no OpenAPI, entao `result.data` ja vem
 * tipado pelo `openapi-fetch`. So resta o `undefined` defensivo (a uniao de status codes do schema
 * inclui respostas sem corpo tipado).
 */
function unexpectedFormatError(status: number): ApiError {
  return new ApiError({
    title: 'A resposta da Api nao tem o formato esperado.',
    status,
    errorCode: 'internal_error',
    correlationId: '',
  })
}

/** Lista os agentes do tenant (id, nome, modo, status). */
export async function listAgents(tenantId: string): Promise<readonly AgentSummary[]> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/agents', {
    params: { path: { tenantId } },
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return result.data
}

/**
 * Muda o modo do agente. ObserveOnly/Suggest sao livres (`confirmAutoReply` ignorado). AutoReply
 * EXIGE `confirmAutoReply: true` E pre-requisitos (§24); os erros conhecidos chegam via
 * `ApiError.problem`: 400 `invalid_agent_mode` (modo invalido), 404 (agente nao encontrado),
 * 400 `auto_reply_confirmation_required` (AutoReply sem confirmacao) e 409 (pre-requisitos nao
 * atendidos — `auto_reply_tenant_not_allowed`/`auto_reply_channel_not_ready`/`auto_reply_agent_not_eligible`).
 */
export async function changeAgentMode(
  tenantId: string,
  agentId: string,
  mode: AgentModeValue,
  confirmAutoReply: boolean,
): Promise<AgentModeChange> {
  const result = await apiClient.POST('/api/v1/tenants/{tenantId}/agents/{agentId}/mode', {
    params: { path: { tenantId, agentId } },
    body: { mode, confirmAutoReply },
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return result.data
}

/**
 * Executa o agente no Playground (nunca envia mensagem real a clientes). Devolve a decisao + trace
 * observavel. Reutilizado pelo passo "Experimente seu agente" do onboarding e pela tela de Agentes.
 */
export async function runPlayground(
  tenantId: string,
  agentId: string,
  message: string,
): Promise<PlaygroundResult> {
  const result = await apiClient.POST('/api/v1/tenants/{tenantId}/agents/{agentId}/playground', {
    params: { path: { tenantId, agentId } },
    body: { message },
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return result.data
}

/** Persona de um agente (nome + descrição + instruções) para criar/editar. */
export interface AgentPersona {
  readonly name: string
  readonly description?: string
  readonly systemPrompt?: string
}

/** Cria um agente (nome + persona), sempre em ObserveOnly. */
export async function createAgent(tenantId: string, persona: AgentPersona): Promise<AgentSummary> {
  const result = await apiClient.POST('/api/v1/tenants/{tenantId}/agents', {
    params: { path: { tenantId } },
    body: persona,
  })
  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }
  return result.data
}

/** Edita a persona do agente. */
export async function updateAgent(
  tenantId: string,
  agentId: string,
  persona: AgentPersona,
): Promise<AgentSummary> {
  const result = await apiClient.PATCH('/api/v1/tenants/{tenantId}/agents/{agentId}', {
    params: { path: { tenantId, agentId } },
    body: persona,
  })
  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }
  return result.data
}

/** Ativa ou desativa o agente. */
export async function setAgentStatus(
  tenantId: string,
  agentId: string,
  enabled: boolean,
): Promise<AgentSummary> {
  const result = await apiClient.POST('/api/v1/tenants/{tenantId}/agents/{agentId}/status', {
    params: { path: { tenantId, agentId } },
    body: { enabled },
  })
  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }
  return result.data
}
